import { motion } from "framer-motion";
import { Play, Pause, ExternalLink } from "lucide-react";

const platforms = [
  { key: "spotify_url", label: "Spotify", testid: "platform-link-spotify" },
  { key: "apple_url", label: "Apple", testid: "platform-link-apple" },
  { key: "youtube_url", label: "YouTube", testid: "platform-link-youtube" },
  { key: "soundcloud_url", label: "SoundCloud", testid: "platform-link-soundcloud" },
  { key: "beatport_url", label: "Beatport", testid: "platform-link-beatport" },
];

export const Releases = ({ tracks, activeTrack, onPlay }) => (
  <section id="releases" className="relative py-28 border-t border-white/10">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="flex items-end justify-between mb-14">
        <div>
          <p className="font-mono-tech text-xs tracking-[0.35em] text-[#00f0ff] uppercase mb-3">// Catalogue</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase text-white">Latest Releases</h2>
        </div>
        <p className="hidden sm:block font-mono-tech text-[11px] tracking-[0.2em] text-slate-500 uppercase text-right">
          Preview 1:00 free<br />Full tracks on all platforms
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {tracks.map((t, i) => {
          const playing = activeTrack?.cat === t.cat;
          return (
            <motion.article
              key={t.cat}
              data-testid={`track-card-${t.cat}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
              className="group bg-[#0c0c12] hover:bg-[#101018] transition-colors"
            >
              <div className="relative aspect-square overflow-hidden">
                <img src={t.cover_url} alt={`${t.title} cover`} className="track-card-img w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <span className="absolute top-4 left-4 font-mono-tech text-[10px] tracking-[0.25em] text-[#00ff66] bg-black/60 px-2 py-1 border border-[#00ff66]/30">
                  {t.cat}
                </span>
                <button
                  data-testid={`track-play-preview-${t.cat}`}
                  onClick={() => onPlay(playing ? null : t)}
                  aria-label={playing ? "Stop preview" : "Play 60 second preview"}
                  className={`absolute bottom-4 right-4 w-14 h-14 flex items-center justify-center transition-all ${playing ? "bg-[#ff0055] text-white" : "bg-[#00f0ff] text-black hover:scale-110"}`}
                >
                  {playing ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" className="ml-0.5" />}
                </button>
                <span className="absolute bottom-6 left-4 font-mono-tech text-[10px] tracking-[0.2em] text-slate-300 uppercase">
                  {playing ? "► Previewing 1:00" : "Preview first 1:00"}
                </span>
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between font-mono-tech text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-2">
                  <span>{t.genre}</span>
                  <span className="text-[#ff0055]">{t.bpm} BPM</span>
                </div>
                <h3 className="font-display text-lg font-bold text-white uppercase">{t.title}</h3>
                <p className="text-sm text-slate-400 mt-1">{t.artist}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {platforms.map((p) => (
                    <a
                      key={p.key}
                      data-testid={`${p.testid}-${t.cat}`}
                      href={t[p.key]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-wider uppercase px-2.5 py-1.5 border border-white/15 text-slate-400 hover:border-[#00f0ff] hover:text-[#00f0ff] transition-colors"
                    >
                      {p.label} <ExternalLink size={10} />
                    </a>
                  ))}
                </div>
              </div>
            </motion.article>
          );
        })}
      </div>
    </div>
  </section>
);
